document.addEventListener('DOMContentLoaded', function () {
  const config = window.OmantelCartCheckoutConfig || {};
  const BRE_URL = config.breUrl || 'https://xhawi.marketcube.io/bre-service/graphql';
  let checkoutValidated = false;
  let isValidatingCheckout = false;

  function getCheckoutButton() {
    return document.querySelector('.cart__checkout-button[name="checkout"]');
  }

  function getCartForm() {
    return document.getElementById('cart');
  }

  if (!getCheckoutButton() || !getCartForm()) return;

  function extractCustomerId(email) {
    const value = String(email || '');
    const localPart = value.split('@')[0] || '';
    const otMatch = localPart.match(/ot[-_.]?(\d+)/i);
    if (otMatch) return otMatch[1];

    const numericLocalPart = localPart.match(/^(\d+)$/);
    if (numericLocalPart) return numericLocalPart[1];

    return '';
  }

  async function hmacHex(keyString, messageString) {
    if (!window.crypto || !window.crypto.subtle) {
      throw new Error('BRE request signing is not available in this browser.');
    }

    const encoder = new TextEncoder();
    const key = await window.crypto.subtle.importKey(
      'raw',
      encoder.encode(String(keyString || '')),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const signature = await window.crypto.subtle.sign('HMAC', key, encoder.encode(messageString));

    return Array.from(new Uint8Array(signature))
      .map(function (byte) { return byte.toString(16).padStart(2, '0'); })
      .join('');
  }

  function getStoredCognitoAccessToken() {
    try {
      return localStorage.getItem('xhawiCognitoAccessToken') || '';
    } catch (e) {
      return '';
    }
  }

  async function buildBreRequestOptions(payload) {
    const auth = window.XHAWI_BRE_AUTH || {};
    const bodyString = JSON.stringify(payload);
    const cognitoAccessToken = getStoredCognitoAccessToken();
    const headers = {
      'Content-Type': 'application/json',
      'X-Xhawi-Customer-Id': String(auth.customerId ?? ''),
      'X-Xhawi-Timestamp': String(auth.timestamp || ''),
      'X-Xhawi-Signature': await hmacHex(auth.pageKey || '', bodyString)
    };

    if (cognitoAccessToken) {
      headers.Authorization = 'Bearer ' + cognitoAccessToken;
    }

    return {
      method: 'POST',
      headers: headers,
      body: bodyString
    };
  }

  function handleUnauthorizedResponse(response) {
    if (response && response.status === 401) {
      if (typeof window.xhawiLogout === 'function') {
        window.xhawiLogout();
      } else {
        window.location.href = '/account/logout?return_url=/account/login';
      }
      return true;
    }
    return false;
  }

  function extractCustomerIdFromCart(cart) {
    const attributes = cart && cart.attributes ? cart.attributes : {};
    const rawCustomerId = prop(attributes, '_customerId');
    if (rawCustomerId) return String(rawCustomerId).replace(/^CDS-/i, '');

    const rawCdsUserId = prop(attributes, '_cdsUserId');
    if (rawCdsUserId) return String(rawCdsUserId).replace(/^CDS-/i, '');

    return '';
  }

  function getRequiredCustomerId(cart) {
    const customerId = extractCustomerId(config.customerEmail);
    if (customerId) return customerId;

    const cartCustomerId = extractCustomerIdFromCart(cart);
    if (cartCustomerId) return cartCustomerId;

    const error = new Error('Customer ID is not present in the customer email.');
    error.customerIdMissing = true;
    alert('Customer ID is not present in the customer email.');
    throw error;
  }

  function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  function prop(properties, key, fallback) {
    if (!properties) return fallback === undefined ? null : fallback;
    const value = properties[key];
    if (value === undefined || value === '') return fallback === undefined ? null : fallback;
    return value;
  }

  function addHiddenProperty(target, key, value) {
    if (value === undefined || value === '') return;
    target[key] = value === null || Array.isArray(value) || typeof value === 'object'
      ? (value === null ? null : JSON.stringify(value))
      : value;
  }

  /**
   * BRE wants upfrontProductAmt / vatProductAmt in OMR on the way in, but sends
   * them back in baisa: AdditionalInfo money fields are integers in baisa
   * ("349000" is 349.000 OMR), while values that already carry a decimal point
   * are OMR. Mirrors normalizeRuleAmount() in omantel-product-builder.js, which
   * writes these same two cart properties at add-to-cart — keep them in step.
   *
   * Without this the Rule 6 response overwrote the OMR cart property with raw
   * baisa, so a second checkout validation of the same cart sent a value 1000x
   * too large.
   */
  function toOmrAmount(value) {
    if (value === null || value === undefined || value === '') return null;
    const raw = String(value).trim().replace(/,/g, '.');
    const amount = Number(raw);
    if (!Number.isFinite(amount)) return null;
    return (raw.indexOf('.') === -1 ? amount / 1000 : amount).toFixed(3);
  }

  function isOmantelLine(item) {
    return !!(
      item
      && item.properties
      && (
        item.properties['Payment plan']
        || item.properties['_paymentNumber']
        || item.properties['Payment number']
        || item.properties['_productConfigNewPct']
      )
    );
  }

  function buildRule6LineItem(item, index) {

    const properties = item.properties || {};
    const paymentPlan = prop(properties, 'Payment plan', '');
    const isInstallment = paymentPlan && paymentPlan !== 'Upfront';
    const isPlanUpgrade = isInstallment && String(prop(properties, '_planAction', '')).toUpperCase() === 'UPGRADE';
    const upgradePlanId = isPlanUpgrade ? (prop(properties, '_newPlanId') || prop(properties, '_planId')) : null;
    const dppProductId = prop(properties, '_dppProductId');
    const hasDeviceProtection = !!dppProductId;

    const lineItem = {
      lineId: String(index + 1),
      skuCode: item.sku || prop(properties, '_skuCode', ''),
      quantity: String(item.quantity || 1),
      productConfigNewPct: prop(properties, '_productConfigNewPct', 'SMARTPHONES'),
      deviceInsuranceProduct: hasDeviceProtection,
      installmentAmount: isInstallment ? prop(properties, '_installmentAmount') : null,
      installmentDuration: isInstallment ? prop(properties, '_installmentDuration', String(paymentPlan).split(' ')[0]) : null,
      installmentAmountBz: isInstallment ? prop(properties, '_installmentAmountBz') : null,
      vatProductAmt: isInstallment ? prop(properties, '_vatProductAmt') : null,
      upfrontProductAmt: isInstallment ? prop(properties, '_upfrontProductAmt') : null,
      vatCollection: isInstallment ? prop(properties, '_vatCollection') : null,
      upfrontCollection: isInstallment ? prop(properties, '_upfrontCollection') : null,
      deviceDiscount: isInstallment ? prop(properties, '_deviceDiscount') : null,
      fullProductAmt: prop(properties, '_fullProductAmt') || (Number(item.price || 0) / 100).toFixed(3),
      langId: '-1',
      selectedPhoneNumber: String(prop(properties, '_paymentNumber') || prop(properties, 'Payment number', '')).split(' - ')[0],
      eshopAdditionalService: prop(properties, '_eshopAdditionalService')
    };

    if (upgradePlanId) {
      lineItem.planId = String(upgradePlanId);
    }

    if (hasDeviceProtection) {
      lineItem.dppProductId = dppProductId;
      lineItem.dppPaymentMode = prop(properties, '_dppPaymentMode');
      lineItem.dppDurationMonths = prop(properties, '_dppDurationMonths');
      lineItem.dppTaxablePrice = prop(properties, '_dppTaxablePrice');
      lineItem.dppOfferPrice = prop(properties, '_dppOfferPrice');
    }

    return lineItem;
  }

  async function getCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to read cart before checkout.');
    return response.json();
  }

  function buildRule6Input(cart) {
    const customerId = getRequiredCustomerId(cart);
    return {
      cdsUserId: 'CDS-' + customerId,
      requestId: generateUUID(),
      transactionId: generateUUID(),
      partnerUserId: 'partner-001',
      cart: {
        cartId: cart.token || 'cart',
        items: (cart.items || []).filter(isOmantelLine).map(buildRule6LineItem)
      },
      channel: 'STOREFRONT'
    };
  }

  function buildRule6ContractProperties(cartItem) {
    cartItem = cartItem && cartItem.additionalInfo ? cartItem.additionalInfo : cartItem;
    const properties = {};
    if (!cartItem) return properties;

    addHiddenProperty(properties, '_billingAccountId', cartItem.billingAccountId);
    addHiddenProperty(properties, '_productCharacteristicNames', cartItem.productCharacteristicNames);
    addHiddenProperty(properties, '_deviceCommitmentId', cartItem.deviceCommitmentId);
    addHiddenProperty(properties, '_productId', cartItem.deviceCommitmentId);
    addHiddenProperty(properties, '_commitmentId', cartItem.deviceCommitmentId);
    addHiddenProperty(properties, '_installmentAmountRule6', cartItem.installmentAmount);
    addHiddenProperty(properties, '_provisioning', cartItem.provisioning);
    addHiddenProperty(properties, '_planCommitment', cartItem.planCommitment);
    addHiddenProperty(properties, '_tariff', cartItem.planId);
    addHiddenProperty(properties, '_noShipmentOrder', false);
    addHiddenProperty(properties, '_outletPickupOrder', false);
    addHiddenProperty(properties, '_pickupOrder', false);
    addHiddenProperty(properties, '_planAction', cartItem.newPlanId ? 'UPGRADE' : 'CURRENT');
    addHiddenProperty(properties, '_planIdRule6', cartItem.planId);
    addHiddenProperty(properties, '_planNameRule6', cartItem.planName);
    addHiddenProperty(properties, '_planStartDate', cartItem.planStartDate);
    addHiddenProperty(properties, '_planEndDate', cartItem.planEndDate);
    addHiddenProperty(properties, '_planDuration', cartItem.planDuration);
    addHiddenProperty(properties, '_planPriceRule6', cartItem.planPrice);
    addHiddenProperty(properties, '_planPriceWithVAT', cartItem.planPriceWithVAT);
    addHiddenProperty(properties, '_planVAT', cartItem.planVAT);
    addHiddenProperty(properties, '_newPlanId', cartItem.newPlanId);
    addHiddenProperty(properties, '_newPlanName', cartItem.newPlanName);
    addHiddenProperty(properties, '_newPlanPrice', cartItem.newPlanPrice);
    addHiddenProperty(properties, '_newPlanPriceWithVAT', cartItem.newPlanPriceWithVAT);
    addHiddenProperty(properties, '_newPlanVAT', cartItem.newPlanVAT);
    addHiddenProperty(properties, '_upfrontProductId', cartItem.upfrontProductId);
    addHiddenProperty(properties, '_vatProductId', cartItem.vatProductId);
    addHiddenProperty(properties, '_upfrontProductAmt', toOmrAmount(cartItem.upfrontProductAmt));
    addHiddenProperty(properties, '_vatProductAmt', toOmrAmount(cartItem.vatProductAmt));
    addHiddenProperty(properties, '_oldDeviceCommitment', cartItem.oldDeviceCommitment);
    addHiddenProperty(properties, '_oldVATCommitment', cartItem.oldVATCommitment);
    addHiddenProperty(properties, '_oldUpfrontCommitment', cartItem.oldUpfrontCommitment);
    addHiddenProperty(properties, '_idNo', cartItem.idNo);
    addHiddenProperty(properties, '_idType', cartItem.idType);
    addHiddenProperty(properties, '_creditRating', cartItem.creditRating);
    addHiddenProperty(properties, '_hasAdditionalInfo', cartItem.hasAdditionalInfo);
    addHiddenProperty(properties, '_hasAdditionalInfoItem', cartItem.hasAdditionalInfo);
    addHiddenProperty(properties, '_hasAdditionalInfoEBU', cartItem.hasAdditionalInfo);
    addHiddenProperty(properties, '_discountProductId', cartItem.discountProductId);
    addHiddenProperty(properties, '_discountProductIdEBU', cartItem.discountProductId);

    return properties;
  }

  function findRule6CartItem(rule6Items, index) {
    const lineId = String(index + 1);
    return (rule6Items || []).find(function (item) {
      const additionalInfo = item && item.additionalInfo ? item.additionalInfo : item;
      return String(additionalInfo && additionalInfo.lineId) === lineId;
    }) || null;
  }

  async function updateCartLineProperties(lineNumber, properties) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        line: lineNumber,
        properties: properties
      })
    });

    if (!response.ok) throw new Error('Unable to update cart details before checkout.');
    return response.json();
  }

  async function updateCartOrderAttributes(cart) {
    const customerId = getRequiredCustomerId(cart);
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        attributes: Object.assign({}, cart.attributes || {}, {
          _customerId: customerId,
          _cdsUserId: 'CDS-' + customerId,
          _xhawiCustomerId: config.xhawiCustomerId || prop(cart.attributes, '_xhawiCustomerId'),
          customerType: prop(cart.attributes, 'customerType'),
          _bulkOrderEBU: false
        })
      })
    });

    if (!response.ok) throw new Error('Unable to update cart attributes before checkout.');
    return response.json();
  }

  async function updateCartPropertiesFromRule6(cart, decision) {
    const rule6Items = decision && Array.isArray(decision.cartItems) ? decision.cartItems : [];
    const cartItems = cart.items || [];

    for (let index = 0; index < cartItems.length; index += 1) {
      const item = cartItems[index];
      if (!isOmantelLine(item)) continue;

      const rule6Item = findRule6CartItem(rule6Items, index);
      if (!rule6Item) continue;

      const existingProperties = item.properties || {};
      const mergedProperties = Object.assign(
        {},
        existingProperties,
        buildRule6ContractProperties(rule6Item)
      );

      if (String(prop(existingProperties, '_planAction', '')).toUpperCase() === 'UPGRADE') {
        mergedProperties._planAction = 'UPGRADE';
        mergedProperties._newPlanId = prop(existingProperties, '_newPlanId') || mergedProperties._newPlanId;
        mergedProperties._newPlanName = prop(existingProperties, '_newPlanName') || mergedProperties._newPlanName;
      }

      delete mergedProperties._customerId;
      delete mergedProperties._cdsUserId;
      delete mergedProperties._paymentNumber;
      delete mergedProperties['Payment number'];

      await updateCartLineProperties(index + 1, mergedProperties);
    }

    await updateCartOrderAttributes(cart);
  }

  async function validateCheckout() {
    const cart = await getCart();
    const omantelItems = (cart.items || []).filter(isOmantelLine);
    if (omantelItems.length === 0) return true;

    const payload = {
      query: 'query rule6EvaluateCheckoutCartValidation($input: CheckoutRequest!) { rule6EvaluateCheckoutCartValidation(input: $input) { meta { status ruleSetVersion ruleSet timestamp service errors { code message messageKey source retryable } } decision { checkoutAllowed rejectionReasons { code message scope blocking lineId } cartItems { additionalInfo { billingAccountId productCharacteristicNames deviceCommitmentId installmentAmount provisioning planCommitment planId planName lineId planStartDate planEndDate planDuration planPrice planPriceWithVAT planVAT newPlanId newPlanName newPlanPrice newPlanPriceWithVAT newPlanVAT upfrontProductId vatProductId upfrontProductAmt vatProductAmt oldDeviceCommitment oldVATCommitment oldUpfrontCommitment idNo idType creditRating hasAdditionalInfo discountProductId } } serviceResult { decisionType code message status } revalidatedAt } } }',
      variables: {
        input: buildRule6Input(cart)
      }
    };

    const response = await fetch(BRE_URL, await buildBreRequestOptions(payload));

    if (handleUnauthorizedResponse(response)) return;
    if (!response.ok) throw new Error('Checkout validation failed. Please try again.');

    const result = await response.json();
    const decision = result
      && result.data
      && result.data.rule6EvaluateCheckoutCartValidation
      ? result.data.rule6EvaluateCheckoutCartValidation.decision
      : null;

    if (!decision || decision.checkoutAllowed !== true) {
      const rejection = decision && decision.rejectionReasons && decision.rejectionReasons[0]
        ? decision.rejectionReasons[0].message
        : null;
      const serviceMessage = decision && decision.serviceResult ? decision.serviceResult.message : null;
      throw new Error(rejection || serviceMessage || 'Checkout is not allowed for this cart.');
    }

    await updateCartPropertiesFromRule6(cart, decision);

    return true;
  }

  async function handleCheckout(event, activeCheckoutButton) {
    if (checkoutValidated || isValidatingCheckout) return;

    const checkoutButton = activeCheckoutButton || getCheckoutButton();
    const cartForm = getCartForm();
    if (!checkoutButton || !cartForm) return;

    event.preventDefault();
    event.stopPropagation();
    isValidatingCheckout = true;
    checkoutButton.disabled = true;
    const originalText = checkoutButton.textContent;
    checkoutButton.textContent = 'Validating...';

    try {
      await validateCheckout();
      checkoutValidated = true;
      checkoutButton.disabled = false;
      checkoutButton.textContent = originalText;

      const currentCheckoutButton = getCheckoutButton();
      const currentCartForm = getCartForm();

      if (
        currentCartForm
        && currentCheckoutButton
        && typeof currentCartForm.requestSubmit === 'function'
        && currentCheckoutButton.form === currentCartForm
      ) {
        currentCartForm.requestSubmit(currentCheckoutButton);
      } else if (currentCheckoutButton) {
        currentCheckoutButton.click();
      } else if (cartForm.requestSubmit && checkoutButton.form === cartForm) {
        cartForm.requestSubmit(checkoutButton);
      } else {
        cartForm.submit();
      }
    } catch (error) {
      checkoutValidated = false;
      if (!error.customerIdMissing) alert(error.message || 'Checkout validation failed.');
      checkoutButton.disabled = false;
      checkoutButton.textContent = originalText;
    } finally {
      isValidatingCheckout = false;
    }
  }

  document.addEventListener('click', function(event) {
    const checkoutButton = event.target.closest('.cart__checkout-button[name="checkout"]');
    if (checkoutButton) handleCheckout(event, checkoutButton);
  });

  document.addEventListener('submit', function(event) {
    if (event.target && event.target.id === 'cart') {
      const checkoutButton = event.submitter && event.submitter.matches('.cart__checkout-button[name="checkout"]')
        ? event.submitter
        : getCheckoutButton();
      if (checkoutButton) handleCheckout(event, checkoutButton);
    }
  });
});
